'use client';

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import type { CountryCode, CountryProfile } from '@/lib/countries';
import { countryProfiles, countryList } from '@/lib/countries';
import {
  getInventoryForCountry,
  getRequisitionsForCountry,
  getConnectionTrackingForCountry,
  getKpisForCountry,
} from '@/lib/country-data';
import type { InventoryItem, Requisition, ConnectionMonthly, KpiCard } from '@/lib/mock-data';
import { connectionsByCountry } from '@/lib/mock-data';
import { useLanguage } from '@/context/LanguageContext';

const VALID_CODES: CountryCode[] = ['GH', 'CI', 'SN', 'TG', 'BJ'];
const STORAGE_KEY = 'wt-country';

interface CountryContextValue {
  code: CountryCode;
  profile: CountryProfile;
  inventory: InventoryItem[];
  requisitions: Requisition[];
  connections: ConnectionMonthly[];
  kpis: KpiCard[];
  countryProfiles: CountryProfile[];
  allCountries: typeof connectionsByCountry;
  setCountry: (code: CountryCode) => void;
}

const CountryContext = createContext<CountryContextValue | null>(null);

export function CountryProvider({ children }: { children: React.ReactNode }) {
  const [code, setCode] = useState<CountryCode>('GH');
  const { setLang, t } = useLanguage();

  // Read from localStorage after mount only — never during render
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && VALID_CODES.includes(stored as CountryCode)) {
        const storedCode = stored as CountryCode;
        setCode(storedCode);
        // Also restore the country's official language
        setLang(countryProfiles[storedCode].officialLanguage);
      }
    } catch {
      // localStorage unavailable (SSR or private mode)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCountry = useCallback((newCode: CountryCode) => {
    setCode(newCode);
    // Always set language to the country's official language
    setLang(countryProfiles[newCode].officialLanguage);
    try {
      localStorage.setItem(STORAGE_KEY, newCode);
    } catch {
      // ignore
    }
  }, [setLang]);

  const profile = useMemo(() => countryProfiles[code], [code]);

  const inventory = useMemo(() => getInventoryForCountry(code), [code]);
  const requisitions = useMemo(() => getRequisitionsForCountry(code), [code]);
  const connections = useMemo(() => getConnectionTrackingForCountry(code), [code]);
  const kpis = useMemo(() => getKpisForCountry(code), [code]);

  // Build toast text using current language
  const handleSetCountry = useCallback((newCode: CountryCode) => {
    setCountry(newCode);
  }, [setCountry]);

  const value = useMemo<CountryContextValue>(
    () => ({
      code,
      profile,
      inventory,
      requisitions,
      connections,
      kpis,
      countryProfiles: countryList,
      allCountries: connectionsByCountry,
      setCountry: handleSetCountry,
    }),
    [code, profile, inventory, requisitions, connections, kpis, handleSetCountry]
  );

  return (
    <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
  );
}

export function useCountry(): CountryContextValue {
  const ctx = useContext(CountryContext);
  if (!ctx) {
    throw new Error('useCountry must be used inside <CountryProvider>');
  }
  return ctx;
}
